import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, tap, catchError, of, finalize, firstValueFrom } from 'rxjs';
import { User } from '../models/auth.model';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { UserApiService } from './user-api.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly router = inject(Router);
  private readonly storage = inject(LocalStorageService);
  private readonly userApiService = inject(UserApiService);

  // Storage keys
  private readonly USER_KEY = 'user';
  private readonly LOGIN_STATUS_KEY = 'userLogin';

  // Signals for reactive state management
  private _isAuthenticated = signal<boolean>(this.hasValidToken());
  private _currentUser = signal<User | null>(this.getStoredUser());
  private _csrfToken = signal<string | null>(null);
  private _isLoading = signal<boolean>(false);
  private _isLoggingOut = false;
  /** Single in-flight CSRF bootstrap so parallel unsafe requests share one refresh */
  private csrfBootstrapPromise: Promise<void> | null = null;

  // Public computed values
  readonly isAuthenticated = computed(() => this._isAuthenticated());
  readonly currentUser = computed(() => this._currentUser());
  readonly csrfToken = computed(() => this._csrfToken());
  readonly isLoading = computed(() => this._isLoading());

  constructor() {
    this.checkAuthStatus();
  }

  /* -------------------- TOKEN MANAGEMENT -------------------- */

  getCsrfToken(): string | null {
    return this._csrfToken();
  }

  setCsrfToken(token: string): void {
    this._csrfToken.set(token);
  }

  private clearTokens(): void {
    this.storage.removeItem(this.USER_KEY);
    this.storage.removeItem(this.LOGIN_STATUS_KEY);
    this.storage.removeItem('dashboard_initial_loading_shown');
  }

  private hasValidToken(): boolean {
    // Since tokens are now in httpOnly cookies, we can't check them directly.
    // We rely on the presence of user data as a heuristic for initial auth state.
    // The auth interceptor will handle 401 errors if the cookie is invalid.
    return !!this.getStoredUser();
  }

  /* -------------------- USER MANAGEMENT -------------------- */

  private getStoredUser(): User | null {
    return this.storage.getItem<User>(this.USER_KEY);
  }

  private setUser(user: User): void {
    this.storage.setItem(this.USER_KEY, user);
    this._currentUser.set(user);
  }

  /* -------------------- AUTH OPERATIONS -------------------- */

  checkAuthStatus(): void {
    const isValid = this.hasValidToken();
    this._isAuthenticated.set(isValid);

    if (!isValid) {
      this.clearTokens();
      this._currentUser.set(null);
    }
  }

  login(user: User, csrfToken?: string): void {
    this.setUser(user);
    if (csrfToken) {
      this.setCsrfToken(csrfToken);
    } else {
      // Fallback: if no CSRF token provided in login response, fetch a fresh one
      this.refreshCsrfToken().subscribe();
    }
    this._isAuthenticated.set(true);
  }

  logout(): Observable<void> {
    if (this._isLoggingOut) {
      return of(undefined);
    }

    this._isLoggingOut = true;
    return this.userApiService.logout().pipe(
      tap(() => {
        this.handleLogoutCleanup();
      }),
      catchError((error) => {
        // Even if API fails, clear local data
        this.handleLogoutCleanup();
        throw error;
      }),
      finalize(() => {
        this._isLoggingOut = false;
      })
    );
  }

  /**
   * Cleans up local data and navigates to the login page after logout
   */
  private handleLogoutCleanup(): void {
    this.clearTokens();
    this._csrfToken.set(null);
    this.csrfBootstrapPromise = null;
    this._isAuthenticated.set(false);
    this._currentUser.set(null);
    this.router.navigate(['/']);
  }



  updateUser(user: Partial<User>): void {
    const currentUser = this._currentUser();
    if (currentUser) {
      const updatedUser = { ...currentUser, ...user };
      this.setUser(updatedUser);
    }
  }
  //added for refreshing csrf token after login and before making any authenticated API calls
  refreshCsrfToken(): Observable<any> {
    return this.userApiService.getCsrfToken().pipe(
      tap((res: any) => {
        if (res && res.data && res.data.csrf_token) {
          this.setCsrfToken(res.data.csrf_token);
        }
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Resolves when a CSRF token is available, or when refresh finished without one.
   * Used by the HTTP interceptor so state-changing requests never fire without waiting
   * for CSRF while still allowing the app shell to bootstrap without blocking.
   */
  ensureCsrfReady(): Promise<void> {
    if (this.getCsrfToken()) {
      return Promise.resolve();
    }
    this.csrfBootstrapPromise ??= firstValueFrom(this.refreshCsrfToken())
      .then(() => undefined)
      .finally(() => {
        this.csrfBootstrapPromise = null;
      });
    return this.csrfBootstrapPromise;
  }

  hasRole(role: string): boolean {
    return this._currentUser()?.role === role;
  }

  isKycVerified(): boolean {
    return this._currentUser()?.isKycVerified ?? false;
  }

  /**
   * Checks if the user's account setup is completed
   * @returns boolean indicating if account setup is completed
   */
  isAccountSetupCompleted(): boolean {
    return this._currentUser()?.accountSetupCompleted ?? false;
  }

  /**
   * Checks if the user's crypto is enabled
   * @returns boolean indicating if crypto is enabled
   */
  isCryptoEnabled(): boolean {
    return this._currentUser()?.is_crypto ?? false;
  }

  /**
   * Checks if the user's sumsub is required
   */
  isSumsubRequired(): boolean {
    return this._currentUser()?.is_sumsub_required ?? true;
  }
}
